import { useRouter } from "next/router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useOrgTimezoneOrUtc } from "~/contexts/orgProvider";
import type { ActionBlockRow } from "~/shared/ai/action-block";
import {
  type ActionIntent,
  type ActionProposalArtifact,
  type ActionProposalItem,
  actionClarifyPicksIntent,
} from "~/shared/ai/assistant-action-artifact";
import { ACTION_ACCENT } from "~/styles/palettes";
import { Anchor, Box, Button, Checkbox, Drawer, Flex, Loader, Text, useMediaQuery } from "~/ui";
import { approveControlLabel, batchRule, cardTitle, headerBadges, selectionEnabledFor, summaryLine } from "./ActionProposalCard";
import { orgHref, ReceiptRowCorrect, receiptAttribution, receiptRowLink } from "./ActionReceipt";
import type { ActionSelectionPatch } from "./action-artifact-state";
import { ActionBlockFooter, ActionBlockHeader } from "./action-block/ActionBlockChrome";
import { ActionBlockGroups, ActionBlockRows, pagedGroupItems } from "./action-block/ActionBlockRows";
import { ActionClarifyPick, ActionRowQuestion } from "./action-block/ActionRowQuestion";
import { BLOCK } from "./action-block/action-block-tokens";
import { ASSISTANT_ACTION_COPY } from "./assistant-action-copy";

const REVIEW_PAGE_SIZE = 50;

const eligibilityOrder: Record<ActionProposalItem["eligibility"], number> = {
  NEEDS_INPUT: 0,
  UNSUPPORTED: 1,
  READY: 2,
};

const receiptOrder = (item: ActionProposalItem): number =>
  item.status === "APPLIED" ? 0 : item.status === "NOT_SELECTED" ? 2 : item.status === "PENDING" ? 3 : 1;

class ProposalItemsLoadError extends Error {}

const loadProposalItems = async (conversationId: string, proposalId: string): Promise<ActionProposalItem[]> => {
  const items: ActionProposalItem[] = [];
  let cursor: number | null = null;
  do {
    const params = new URLSearchParams({ conversationId, proposalId });
    if (cursor !== null) {
      params.set("cursor", String(cursor));
    }
    const response = await fetch(`/api/rest/assistant-action-proposal-items?${params.toString()}`);
    if (!response.ok) {
      throw new ProposalItemsLoadError();
    }
    const page: { items: ActionProposalItem[]; nextCursor: number | null } = await response.json();
    items.push(...page.items);
    cursor = page.nextCursor;
  } while (cursor !== null);
  return items;
};

/**
 * The review beside the conversation (or full width below 1024px): sticky header with the counts,
 * a scrolling list of block rows, and a sticky footer that carries approval. A finished proposal
 * renders the same rows as a receipt with one action set per saved row.
 */
export const ActionBatchReview = ({
  opened,
  onClose,
  artifact,
  conversationId,
  patch,
  onStage,
  onSubmitAction,
  onChangeDetails,
  onCorrect,
  onClarify,
  controlError,
  embedded = false,
}: {
  opened: boolean;
  onClose: () => void;
  artifact: ActionProposalArtifact | null;
  conversationId: string | null;
  patch: ActionSelectionPatch;
  onStage: (patch: ActionSelectionPatch) => void;
  onSubmitAction: (query: string, intent: ActionIntent) => Promise<void>;
  onChangeDetails?: () => void;
  onCorrect?: (item: ActionProposalItem) => void;
  onClarify?: (query: string, picks?: readonly ActionClarifyPick[]) => Promise<void>;
  /** Why the last press on this review did not go through. */
  controlError?: string;
  embedded?: boolean;
}) => {
  const router = useRouter();
  const orgTimezone = useOrgTimezoneOrUtc();
  const isNarrow = useMediaQuery("(max-width: 1023px)");
  const proposalId = artifact?.proposalId ?? null;
  const proposalVersion = artifact?.version ?? null;
  const inlineItems = artifact?.inlineItems;
  const receiptMode =
    artifact?.status === "APPLIED" ||
    artifact?.status === "PARTIALLY_APPLIED" ||
    artifact?.status === "STALE" ||
    artifact?.status === "FAILED";
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [page, setPage] = useState(1);
  // A many-choice question's ticks before its own submit; nothing durable exists until it is sent.
  const [chosenChoices, setChosenChoices] = useState<Record<string, ReadonlySet<string>>>({});

  useEffect(() => {
    if (embedded && opened) {
      headingRef.current?.focus();
    }
  }, [embedded, opened, proposalId, proposalVersion]);
  useEffect(() => {
    setPage(1);
    setChosenChoices({});
  }, [proposalId, proposalVersion]);
  const [items, setItems] = useState<ActionProposalItem[]>(() => artifact?.inlineItems ?? []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loadFailed = artifact?.labels.loadFailed ?? null;

  useEffect(() => {
    if (!opened || !proposalId) {
      return;
    }
    setError(null);
    if (inlineItems) {
      setItems(inlineItems);
      setLoading(false);
      return;
    }
    if (!conversationId) {
      setItems([]);
      setLoading(false);
      setError("The active Assistant conversation is unavailable.");
      return;
    }
    let active = true;
    setItems([]);
    setLoading(true);
    void loadProposalItems(conversationId, proposalId)
      .then(next => {
        if (active) {
          setItems(next);
        }
      })
      .catch(() => {
        if (active) {
          setError(loadFailed);
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [conversationId, inlineItems, opened, proposalId, proposalVersion, receiptMode, loadFailed]);

  const grouped = Boolean(artifact?.groups) && !receiptMode;
  // An ungrouped review folds its left-out rows into one trailing line instead of paging them among the rest.
  const foldItems = useMemo(() => (grouped ? [] : items.filter(item => item.row.state === "left_out")), [grouped, items]);
  const foldLines = grouped ? [] : (artifact?.extraRows ?? []).flatMap(row => (row.group === "left_out" && "text" in row ? [row] : []));
  // Rows that need a person come first, then the largest moves; a receipt leads with what saved.
  const ordered = useMemo(
    () =>
      (grouped ? pagedGroupItems(artifact?.groups, items) : items.filter(item => item.row.state !== "left_out")).sort((left, right) => {
        const group = receiptMode
          ? receiptOrder(left) - receiptOrder(right)
          : eligibilityOrder[left.eligibility] - eligibilityOrder[right.eligibility];
        if (group !== 0) {
          return group;
        }
        return (right.sortKey ?? 0) - (left.sortKey ?? 0) || left.ordinal - right.ordinal;
      }),
    [items, receiptMode, grouped, artifact?.groups],
  );
  const pageCount = Math.max(1, Math.ceil(ordered.length / REVIEW_PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const pageItems = ordered.slice((currentPage - 1) * REVIEW_PAGE_SIZE, currentPage * REVIEW_PAGE_SIZE);
  const rows = useMemo(() => pageItems.map(item => item.row), [pageItems]);

  if (!artifact) {
    return null;
  }
  const binding = artifact.binding;
  const selectionEnabled = selectionEnabledFor(artifact);
  const selected = selectionEnabled
    ? artifact.counts.selected + patch.includeItemKeys.length - patch.excludeItemKeys.length
    : artifact.counts.selected;
  const orgSlug = typeof router.query.orgSlug === "string" ? router.query.orgSlug : "";
  const canRefine = artifact.refinement?.available !== false;
  const canControl = artifact.status === "PENDING" && binding !== undefined;
  const itemsByKey = new Map(pageItems.map(item => [item.itemKey, item]));
  const allItemsByKey = new Map(items.map(item => [item.itemKey, item]));
  const toggleChoice = (itemKey: string, choiceId: string) =>
    setChosenChoices(previous => {
      const next = new Set(previous[itemKey] ?? []);
      if (next.has(choiceId)) {
        next.delete(choiceId);
      } else {
        next.add(choiceId);
      }
      return { ...previous, [itemKey]: next };
    });
  // Each question answers through its own submission: a one-choice pick applies at once, a many-choice question sends
  // its ticks with its own submit, and a reply-kind answer posts the person's visible turn.
  const pickAtOnce = (picks: readonly ActionClarifyPick[]) => {
    const mirror = ASSISTANT_ACTION_COPY.chosenRowsTurn(
      picks.map(pick => ({ subject: allItemsByKey.get(pick.itemKey)?.row.subject ?? pick.label, label: pick.label, reply: pick.reply })),
    );
    if (binding) {
      return onSubmitAction(
        mirror,
        actionClarifyPicksIntent({ proposalId: artifact.proposalId, version: artifact.version, token: binding.token }, picks),
      );
    }
    return onClarify ? onClarify(mirror, picks) : Promise.resolve();
  };
  const setIncluded = (item: ActionProposalItem, checked: boolean) => {
    const include = new Set(patch.includeItemKeys);
    const exclude = new Set(patch.excludeItemKeys);
    if (checked === item.selected) {
      include.delete(item.itemKey);
      exclude.delete(item.itemKey);
    } else if (checked) {
      include.add(item.itemKey);
      exclude.delete(item.itemKey);
    } else {
      exclude.add(item.itemKey);
      include.delete(item.itemKey);
    }
    onStage({
      includeItemKeys: [...include],
      excludeItemKeys: [...exclude],
      selectedItemKeys: items
        .filter(
          candidate =>
            candidate.eligibility === "READY" &&
            (include.has(candidate.itemKey) || (candidate.selected && !exclude.has(candidate.itemKey))),
        )
        .map(candidate => candidate.itemKey),
    });
  };
  const renderLead =
    !receiptMode && selectionEnabled && artifact.status === "PENDING"
      ? (row: ActionBlockRow) => {
          const item = itemsByKey.get(row.key);
          if (!item || item.eligibility !== "READY") {
            return null;
          }
          const checked = patch.includeItemKeys.includes(item.itemKey)
            ? true
            : patch.excludeItemKeys.includes(item.itemKey)
              ? false
              : item.selected;
          return (
            <Checkbox
              aria-label={`Include ${row.subject}${row.secondary ? ` · ${row.secondary}` : ""}`}
              checked={checked}
              onChange={event => setIncluded(item, event.currentTarget.checked)}
            />
          );
        }
      : undefined;
  const saved = artifact.status === "APPLIED" || artifact.status === "PARTIALLY_APPLIED";
  const renderActions = saved
    ? (row: ActionBlockRow) => (
        <ReceiptRowCorrect
          item={itemsByKey.get(row.key)}
          correctLabel={artifact.labels.correct}
          onCorrect={canRefine ? onCorrect : undefined}
        />
      )
    : undefined;
  const subjectLink = saved ? (row: ActionBlockRow) => receiptRowLink(itemsByKey.get(row.key), orgSlug) : undefined;
  const renderQuestion =
    !receiptMode && canControl && onClarify
      ? (row: ActionBlockRow) => {
          const item = itemsByKey.get(row.key);
          if (!item || item.eligibility !== "NEEDS_INPUT" || !item.question) {
            return null;
          }
          return (
            <ActionRowQuestion
              itemKey={item.itemKey}
              subject={item.row.subject}
              question={item.question}
              canControl={canControl}
              chosenSet={chosenChoices[item.itemKey] ?? new Set<string>()}
              onToggleChoice={choiceId => toggleChoice(item.itemKey, choiceId)}
              onClarify={onClarify}
              onStructuredClarify={pickAtOnce}
            />
          );
        }
      : undefined;
  const title = cardTitle(artifact);
  const rule = grouped || receiptMode ? null : batchRule(artifact);
  const summary = rule ? `${summaryLine(artifact)} · ${rule}` : summaryLine(artifact);
  // A saved review says who saved it and what it holds; a review waiting to be saved carries no counts beside its button.
  const footerNote =
    grouped && artifact.labels.footer ? artifact.labels.footer : receiptMode ? receiptAttribution(artifact, orgTimezone) : null;

  const headingId = `action-review-title-${artifact.proposalId}`;
  const content = (
    <Flex
      direction="column"
      role="region"
      aria-labelledby={headingId}
      data-action-review=""
      data-review-status={artifact.status}
      style={{ height: "100%", minHeight: 0 }}
      onKeyDown={event => {
        if (embedded && event.key === "Escape") {
          event.stopPropagation();
          onClose();
        }
      }}
    >
      <ActionBlockHeader
        title={title}
        titleId={headingId}
        badges={headerBadges(artifact)}
        summary={summary}
        density="pane"
        headingRef={headingRef}
        aside={
          embedded || receiptMode ? (
            <Flex align="center" gap={12} justify="flex-end">
              {receiptMode &&
                artifact.headerLinks.map(link => (
                  <Anchor key={link.label} href={orgHref(orgSlug, link.href)} style={{ fontSize: BLOCK.bodySize, fontWeight: 500 }}>
                    {link.label}
                  </Anchor>
                ))}
              {embedded && (
                <Button size="xs" variant="subtle" color="gray" onClick={onClose}>
                  {isNarrow ? "Back to conversation" : "Close"}
                </Button>
              )}
            </Flex>
          ) : undefined
        }
      />
      <Box style={{ flex: "1 1 auto", minHeight: 0, overflow: "auto" }}>
        {loading && (
          <Flex align="center" gap="sm" style={{ padding: 20 }}>
            <Loader size="sm" />
            <Text style={{ fontSize: BLOCK.bodySize, color: BLOCK.secondary }}>Loading</Text>
          </Flex>
        )}
        {(error ?? controlError) && (
          <Text role="alert" style={{ padding: 20, fontSize: BLOCK.bodySize, color: BLOCK.body }}>
            {error ?? controlError}
          </Text>
        )}
        {!loading &&
          !error &&
          (grouped && artifact.groups ? (
            <ActionBlockGroups
              groups={artifact.groups}
              extraRows={artifact.extraRows ?? []}
              items={items}
              pageItems={pageItems}
              canControl={canControl}
              onClarify={onClarify}
              renderItems={groupItems => (
                <ActionBlockRows
                  rows={groupItems.map(item => item.row)}
                  mode="review"
                  layout={isNarrow ? "stacked" : "wide"}
                  density="pane"
                  renderLead={renderLead}
                  renderQuestion={renderQuestion}
                />
              )}
            />
          ) : (
            <ActionBlockRows
              rows={rows}
              mode={receiptMode ? "receipt" : "review"}
              layout={isNarrow ? "stacked" : "wide"}
              density="pane"
              renderLead={renderLead}
              renderQuestion={renderQuestion}
              renderActions={renderActions}
              subjectLink={subjectLink}
            />
          ))}
        {!loading && !error && foldItems.length + foldLines.length > 0 && (
          <details style={{ padding: "12px 20px", borderTop: `1px solid ${BLOCK.divider}` }}>
            <summary style={{ cursor: "pointer", fontSize: BLOCK.bodySize, color: BLOCK.secondary }}>
              {ASSISTANT_ACTION_COPY.leftOut(foldItems.length + foldLines.length)}
            </summary>
            {foldItems.length > 0 && (
              <ActionBlockRows
                rows={foldItems.map(item => item.row)}
                mode={receiptMode ? "receipt" : "review"}
                layout={isNarrow ? "stacked" : "wide"}
                density="pane"
              />
            )}
            {foldLines.map(line => (
              <Text key={line.key} style={{ padding: "6px 0 0", fontSize: BLOCK.meta, color: BLOCK.secondary }}>
                {line.text}
              </Text>
            ))}
          </details>
        )}
        {!loading && !error && pageCount > 1 && (
          <Flex align="center" justify="center" gap="sm" style={{ padding: 12, borderTop: `1px solid ${BLOCK.divider}` }}>
            <Button size="xs" variant="subtle" color="gray" disabled={currentPage <= 1} onClick={() => setPage(currentPage - 1)}>
              Previous
            </Button>
            <Text style={{ fontSize: BLOCK.meta, color: BLOCK.secondary }}>
              Page {currentPage} of {pageCount}
            </Text>
            <Button size="xs" variant="subtle" color="gray" disabled={currentPage >= pageCount} onClick={() => setPage(currentPage + 1)}>
              Next
            </Button>
          </Flex>
        )}
      </Box>
      {!canRefine && (onChangeDetails || onCorrect) && (
        <Text style={{ padding: "8px 20px 0", fontSize: BLOCK.meta, color: BLOCK.secondary }}>
          {ASSISTANT_ACTION_COPY.inactiveConversation}
        </Text>
      )}
      <ActionBlockFooter density="pane" note={footerNote}>
        {artifact.status === "PENDING" && binding && selected > 0 && artifact.policy.approvable && (
          <Button
            size={isNarrow ? "md" : "sm"}
            fullWidth={isNarrow}
            data-action-control="approve"
            style={{ backgroundColor: ACTION_ACCENT }}
            disabled={loading || Boolean(error)}
            onClick={() =>
              void onSubmitAction(selected === 1 ? ASSISTANT_ACTION_COPY.approveOneTurn : ASSISTANT_ACTION_COPY.approveManyTurn, {
                kind: "approve",
                selectedItemKeys: selectionEnabled
                  ? items
                      .filter(
                        item =>
                          item.eligibility === "READY" &&
                          (patch.includeItemKeys.includes(item.itemKey) ||
                            (item.selected && !patch.excludeItemKeys.includes(item.itemKey))),
                      )
                      .map(item => item.itemKey)
                  : undefined,
                proposalId: artifact.proposalId,
                version: artifact.version,
                token: binding.token,
              })
            }
          >
            {approveControlLabel(artifact, selected)}
          </Button>
        )}
        {/* An open question already shows in the pane; the footer does not repeat it. */}
        {artifact.status === "PENDING" &&
          binding &&
          !artifact.policy.approvable &&
          artifact.policy.reason &&
          artifact.counts.needsInput === 0 && (
            <Text style={{ fontSize: BLOCK.meta, color: BLOCK.secondary }}>{artifact.policy.reason}</Text>
          )}
        {artifact.status === "PENDING" && binding && onChangeDetails && canRefine && (
          <Button size="sm" variant="subtle" data-action-control="change" onClick={onChangeDetails}>
            {artifact.labels.change}
          </Button>
        )}
        {artifact.status === "PENDING" && binding && (
          <Button
            size="sm"
            variant="subtle"
            color="gray"
            style={{ color: BLOCK.secondary }}
            data-action-control="dismiss"
            onClick={() =>
              void onSubmitAction(ASSISTANT_ACTION_COPY.dismissTurn, {
                kind: "dismiss",
                proposalId: artifact.proposalId,
                version: artifact.version,
                token: binding.token,
              })
            }
          >
            {ASSISTANT_ACTION_COPY.dismiss}
          </Button>
        )}
      </ActionBlockFooter>
    </Flex>
  );
  if (embedded) {
    return (
      <Box
        style={{
          height: "100%",
          minWidth: 0,
          border: `1px solid ${BLOCK.border}`,
          borderRadius: BLOCK.radius,
          background: "#FFFFFF",
          boxShadow: "0px 1px 3px rgba(16, 24, 40, 0.1), 0px 1px 2px -2px rgba(16, 24, 40, 0.1)",
          overflow: "hidden",
        }}
      >
        {content}
      </Box>
    );
  }
  return (
    <Drawer position="right" size={isNarrow ? "100vw" : "66vw"} opened={opened} onClose={onClose} title={title}>
      {content}
    </Drawer>
  );
};
